import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchFinanceContactsAPI, updateFinanceContactsAPI } from '../../services/api/contactsService';
import { FinanceContacts } from '../../types/contacts/contacts.types';
import { RootState } from '../store';

// Define the state interface
interface ContactsState {
  financeContacts: FinanceContacts[];
  financeContactsIsLoading: boolean;
  financeContactsError: string | null;
  financeContactsHasAttemptedFetch: boolean;
  financeContactsLastFetchTimestamp: number | null;
}

// Initial state
const initialState: ContactsState = {
  financeContacts: [],
  financeContactsIsLoading: false,
  financeContactsError: null,
  financeContactsHasAttemptedFetch: false,
  financeContactsLastFetchTimestamp: null,
};

export const fetchFinanceContacts = createAsyncThunk(
  'contacts/fetchFinanceContacts',
  async (_, { rejectWithValue }) => {
    try {
      return (await fetchFinanceContactsAPI()) as FinanceContacts[];
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch finance contacts');
    }
  }
);

export const updateFinanceContacts = createAsyncThunk(
  'contacts/updateFinanceContacts',
  async (financeContact: FinanceContacts, { rejectWithValue }) => {
    try {
      return await updateFinanceContactsAPI(financeContact);
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to update finance contacts');
    }
  }
);

// Create the slice
const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    clearFinanceContactsError: (state) => {
      state.financeContactsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchFinanceContacts
      .addCase(fetchFinanceContacts.pending, (state) => {
        state.financeContactsIsLoading = true;
        state.financeContactsError = null;
      })
      .addCase(fetchFinanceContacts.fulfilled, (state, action) => {
        state.financeContactsIsLoading = false;
        state.financeContacts = action.payload;
        state.financeContactsHasAttemptedFetch = true;
        state.financeContactsLastFetchTimestamp = Date.now();
      })
      .addCase(fetchFinanceContacts.rejected, (state, action) => {
        state.financeContactsIsLoading = false;
        state.financeContactsError = action.payload as string;
        state.financeContactsHasAttemptedFetch = true;
      })
      // updateFinanceContacts
      .addCase(updateFinanceContacts.pending, (state) => {
        state.financeContactsIsLoading = true;
        state.financeContactsError = null;
      })
      .addCase(updateFinanceContacts.fulfilled, (state) => {
        state.financeContactsIsLoading = false;
      })
      .addCase(updateFinanceContacts.rejected, (state, action) => {
        state.financeContactsIsLoading = false;
        state.financeContactsError = action.payload as string;
      });
  },
});

// Export selectors
export const selectFinanceContacts = (state: RootState) => {
  const contactsState = state.contacts as unknown as ContactsState;
  return contactsState.financeContacts;
};
export const selectFinanceContactsIsLoading = (state: RootState) => {
  const contactsState = state.contacts as unknown as ContactsState;
  return contactsState.financeContactsIsLoading;
};
export const selectFinanceContactsError = (state: RootState) => {
  const contactsState = state.contacts as unknown as ContactsState;
  return contactsState.financeContactsError;
};
export const selectFinanceContactsHasAttemptedFetch = (state: RootState) => {
  const contactsState = state.contacts as unknown as ContactsState;
  return contactsState.financeContactsHasAttemptedFetch;
};
export const selectFinanceContactsLastFetchTimestamp = (state: RootState) => {
  const contactsState = state.contacts as unknown as ContactsState;
  return contactsState.financeContactsLastFetchTimestamp;
};

// Export actions
export const { clearFinanceContactsError } = contactsSlice.actions;

// Export reducer
export default contactsSlice.reducer;
